
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchPremiumBotAccounts, togglePremiumBotAccountStatus, deletePremiumBotAccount } from '@/services/premium-bot';
import { useAuth } from '@/contexts/AuthContext';
import { DetailedBotAccount } from '@/types/trader';
import { useState } from 'react';
import { toast } from 'sonner';

export const usePremiumBotAccounts = (botId: string | undefined) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [disconnectedCspAccountId, setDisconnectedCspAccountId] = useState<string | undefined>(undefined);
  const [isDisconnectedDialogOpen, setIsDisconnectedDialogOpen] = useState(false);
  const [deletingAccountIds, setDeletingAccountIds] = useState<Set<string>>(new Set());
  
  const {
    data: connectedAccounts = [],
    isLoading,
    error,
    refetch
  } = useQuery<DetailedBotAccount[]>({
    queryKey: ['premiumBotAccounts', botId, user?.id],
    queryFn: async () => {
      if (!botId || !user?.id) return [];
      return fetchPremiumBotAccounts(botId, user.id);
    },
    enabled: !!botId && !!user?.id
  });
  
  const toggleConnectionMutation = useMutation({
    mutationFn: async ({ 
      accountId, 
      isConnected 
    }: { 
      accountId: string; 
      isConnected: boolean 
    }) => {
      if (!botId || !user?.id) throw new Error("Missing required data");
      return togglePremiumBotAccountStatus(accountId, isConnected);
    },
    onSuccess: async (_, variables) => {
      // Use refetchQueries for immediate refresh instead of invalidateQueries
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['resourceUsage', user?.id] }),
        queryClient.refetchQueries({ queryKey: ['premiumBotAccounts', botId, user?.id] }),
        queryClient.refetchQueries({ queryKey: ['premiumBotDetail', botId, user?.id] })
      ]);
      
      toast.success(
        variables.isConnected ? "Account activated" : "Account deactivated",
        {
          description: variables.isConnected 
            ? "The trading account is now connected" 
            : "The trading account is now disconnected"
        }
      );
    },
    onError: (error: any, variables) => {
      console.error("Error toggling connection:", error);
      
      const errorMessage = error?.message || '';
      if (
        error?.code === 'P0001' || 
        errorMessage.includes('disconnected in the Ctrader Profile')
      ) {
        const account = connectedAccounts.find(acc => acc.id === variables.accountId);
        if (account?.api_connection?.csp_account_id) {
          setDisconnectedCspAccountId(account.api_connection.csp_account_id);
          setIsDisconnectedDialogOpen(true);
        } else {
          toast.error("Connection Error", {
            description: "This account has been disconnected in the CTrader Profile. Please reconnect it."
          });
        }
      } else {
        toast.error("Connection Error", {
          description: "Failed to update account connection status. Please try again."
        });
      }
    }
  });
  
  const deleteAccountMutation = useMutation({
    mutationFn: async (accountId: string) => {
      if (!botId || !user?.id) throw new Error("Missing required data");
      return deletePremiumBotAccount(accountId);
    },
    onMutate: async (accountId: string) => {
      // Optimistic update: immediately remove account from UI
      setDeletingAccountIds(prev => new Set([...prev, accountId]));
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['premiumBotAccounts', botId, user?.id] });
      
      // Snapshot the previous value
      const previousAccounts = queryClient.getQueryData<DetailedBotAccount[]>(['premiumBotAccounts', botId, user?.id]);
      
      // Optimistically update to the new value
      if (previousAccounts) {
        queryClient.setQueryData<DetailedBotAccount[]>(
          ['premiumBotAccounts', botId, user?.id], 
          previousAccounts.filter(account => account.id !== accountId)
        );
      }
      
      // Return a context object with the snapshotted value
      return { previousAccounts };
    },
    onSuccess: async () => {
      // Use refetchQueries for immediate refresh
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['resourceUsage', user?.id] }),
        queryClient.refetchQueries({ queryKey: ['premiumBotAccounts', botId, user?.id] }),
        queryClient.refetchQueries({ queryKey: ['premiumBotDetail', botId, user?.id] })
      ]);
      
      toast.success("Account removed", {
        description: "Trading account has been removed from this bot"
      });
    },
    onError: (error, accountId, context) => {
      // Rollback optimistic update on error
      if (context?.previousAccounts) {
        queryClient.setQueryData<DetailedBotAccount[]>(
          ['premiumBotAccounts', botId, user?.id], 
          context.previousAccounts
        );
      }
      
      toast.error("Removal Error", {
        description: "Failed to remove the trading account. Please try again."
      });
    },
    onSettled: (_, __, accountId) => {
      // Always clean up the deleting state
      setDeletingAccountIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  });
  
  const handleToggleConnection = (accountId: string, isConnected: boolean) => {
    toggleConnectionMutation.mutate({ 
      accountId, 
      isConnected 
    });
  };
  
  const handleDeleteAccount = (accountId: string) => {
    deleteAccountMutation.mutate(accountId);
  };
  
  return {
    connectedAccounts,
    isLoading,
    error,
    refetch,
    handleToggleConnection,
    handleDeleteAccount,
    isToggling: toggleConnectionMutation.isPending,
    isDeleting: deleteAccountMutation.isPending,
    deletingAccountIds,
    disconnectedCspAccountId,
    isDisconnectedDialogOpen,
    setIsDisconnectedDialogOpen
  };
};
